import React, { useState } from 'react';
import { ChevronDown, Columns3 } from 'lucide-react';
import QawaemTableCheckbox from './QawaemTableCheckbox';
import { QAWAEM_DROPDOWN_PANEL_STYLE, QAWAEM_TABLE_TOOLBAR_FILTER_BTN } from '../utils/qawaemDropdownStyles';
import {
  ToolbarDropdownPortal,
  useToolbarDropdownDismiss,
  useToolbarDropdownPortal,
} from './toolbarDropdownPortal';

/** Console TableToolbarColumnPicker — multi-select column visibility */
export default function QawaemTableToolbarColumnPicker({
  columns,
  onToggle,
  visibleCount,
  totalCount,
  align = 'start',
  /** 'toolbar' = full label button; 'icon' = compact icon for table header */
  variant = 'toolbar',
}) {
  const [open, setOpen] = useState(false);
  const { anchorRef, panelRef, pos } = useToolbarDropdownPortal(open, align, 'content', 240);

  useToolbarDropdownDismiss(open, () => setOpen(false), anchorRef, panelRef);

  const iconOnly = variant === 'icon';
  const pickerTitle = `الأعمدة (${visibleCount}/${totalCount}) — إظهار / إخفاء أعمدة الجدول`;

  return (
    <div className={`relative shrink-0 ${iconOnly ? 'inline-flex' : ''}`} ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={
          iconOnly
            ? 'p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors'
            : `${QAWAEM_TABLE_TOOLBAR_FILTER_BTN} justify-between`
        }
        style={iconOnly ? undefined : { minWidth: '132px' }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={pickerTitle}
        title={pickerTitle}
      >
        {iconOnly ? (
          <Columns3 size={16} className="shrink-0" />
        ) : (
          <>
            <span className="flex items-center gap-1.5 min-w-0">
              <Columns3 size={16} className="text-console-muted shrink-0" />
              <span className="truncate">
                الأعمدة ({visibleCount}/{totalCount})
              </span>
            </span>
            <ChevronDown size={14} className={`text-console-muted shrink-0 ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>
      <ToolbarDropdownPortal
        open={open}
        panelRef={panelRef}
        pos={pos}
        role="listbox"
        className="border border-teal-500/35 rounded-2xl shadow-2xl overflow-y-auto"
        style={{
          background: QAWAEM_DROPDOWN_PANEL_STYLE.background,
          backdropFilter: QAWAEM_DROPDOWN_PANEL_STYLE.backdropFilter,
          WebkitBackdropFilter: QAWAEM_DROPDOWN_PANEL_STYLE.WebkitBackdropFilter,
          boxShadow: QAWAEM_DROPDOWN_PANEL_STYLE.boxShadow,
          maxHeight: 'min(360px, 70vh)',
          minWidth: '220px',
        }}
      >
        <div className="px-3 pt-3 pb-1.5 border-b border-[#6e9b6e]/15">
          <p className="text-xs font-bold text-console flex items-center gap-1.5">
            <Columns3 size={14} className="text-[#6e9b6e]" />
            إظهار الأعمدة
          </p>
        </div>
        <div className="p-2">
          {columns.map((col) => {
            const disabled = Boolean(col.required);
            return (
              <button
                key={col.key}
                type="button"
                role="option"
                aria-selected={col.checked}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) onToggle(col.key);
                }}
                className={`w-full px-2.5 py-2 text-start rounded-lg text-sm font-medium transition-colors flex items-center gap-3 ${
                  disabled
                    ? 'opacity-55 cursor-not-allowed text-console-muted'
                    : 'text-console hover:bg-white/60'
                }`}
                title={col.label}
              >
                <QawaemTableCheckbox
                  checked={col.checked}
                  disabled={disabled}
                  size="lg"
                  className="pointer-events-none"
                />
                <span className="truncate flex-1 min-w-0 leading-snug">
                  {col.label}
                  {col.required ? <span className="text-console-muted font-normal text-xs"> (مطلوب)</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </ToolbarDropdownPortal>
    </div>
  );
}
